import { useRef, useState } from 'react';
import { Link } from 'react-router';
import {
  AlertTriangle,
  ArrowRight,
  Banknote,
  BookOpen,
  CalendarClock,
  Camera,
  ChevronDown,
  ClipboardCheck,
  Clock4,
  FileSpreadsheet,
  Landmark,
  PenLine,
  PiggyBank,
  Ruler,
  Scale,
  ShieldQuestion,
  X,
} from 'lucide-react';
import { APP_NAME } from '@helfr/domain';
import { NahledAplikace } from '../components/NahledAplikace.js';
import { VerejnaHlavicka } from '../components/VerejnaHlavicka.js';

/**
 * SCÉNÁŘE — vizuální „dokumentace" veřejného webu: vlevo menu nejčastějších
 * problémů ze stavby, vpravo krok-za-krokem řešení z pohledu jednotlivých
 * rolí (mistr, přípravář, TDS, subdodavatel, ředitel) se skutečnými náhledy
 * aplikace. Zásada důvěry: scénáře popisují, jak funkce fungují — žádné
 * sliby úspor ani vymyšlená čísla.
 */

type Role = 'mistr' | 'pripravar' | 'reditel' | 'tds' | 'subdodavatel';

/** Barevné odlišení rolí — čtenář sleduje „kdo je na tahu" barvou.
 * Exportováno i pro prerender (JSON-LD FAQPage a llms.txt). */
export const ROLE: Record<Role, { nazev: string; chip: string; tecka: string }> = {
  mistr: { nazev: 'Mistr na stavbě', chip: 'bg-accent text-text-heading', tecka: 'bg-accent' },
  pripravar: { nazev: 'Přípravář', chip: 'bg-info-text text-white', tecka: 'bg-info-text' },
  reditel: { nazev: 'Ředitel', chip: 'bg-primary text-white', tecka: 'bg-primary' },
  tds: { nazev: 'TDS / dozor', chip: 'bg-sporna-text text-white', tecka: 'bg-sporna-text' },
  subdodavatel: {
    nazev: 'Subdodavatel',
    chip: 'bg-success-text text-white',
    tecka: 'bg-success-text',
  },
};

interface Krok {
  role: Role;
  text: string;
  obrazek?: string;
  alt?: string;
}

type Skupina = 'Peníze a vícepráce' | 'Deník a důkazy' | 'Papíry a lidé';

interface Scenar {
  id: string;
  skupina: Skupina;
  Ikona: typeof BookOpen;
  problem: string;
  /** Jak to bolí dnes — jedna věta, kterou stavař pozná. */
  dnes: string;
  kroky: Krok[];
  /** Co na konci drží v ruce. */
  vysledek: string;
}

/** Exportováno i pro prerender (entry-prerender.tsx) — JSON-LD FAQPage a llms.txt. */
export const SCENARE: Scenar[] = [
  {
    id: 'ztracena-viceprace',
    skupina: 'Peníze a vícepráce',
    Ikona: Banknote,
    problem: 'Vícepráce se ztratila a nikdo ji nevyfakturoval',
    dnes: 'Chlapi něco navíc udělali, řeklo se to v autě po telefonu — a za tři měsíce si na to při fakturaci už nikdo nevzpomene.',
    kroky: [
      {
        role: 'mistr',
        text: 'Při výkopu narazí na starou jímku, která není v projektu. Vyfotí ji a třemi ťuknutími nahlásí nález — i bez signálu (odešle se, až signál bude).',
        obrazek: '/marketing/teren-denik.png',
        alt: 'Terénní deník mistra — velká tlačítka NÁLEZ, VÝZVA a PODEPSAT',
      },
      {
        role: 'pripravar',
        text: 'Nález mu přistane ve frontě s fotkou a stářím. Rozhodne: je to vícepráce → jedním klikem z něj udělá Změnu.',
        obrazek: '/marketing/nalezy.png',
        alt: 'Fronta nálezů — karta s fotkou, přepisem a rozhodnutím „Je to vícepráce"',
      },
      {
        role: 'pripravar',
        text: 'Ocení obě strany: změnový list na investora (výnos) i na subdodavatele (náklad). Marže je vidět hned — a hlídač nedovolí, aby náklad zůstal bez výnosového protějšku.',
        obrazek: '/marketing/zmena.png',
        alt: 'Detail změny — co fakturujeme, co nás to stojí, marže uprostřed',
      },
      {
        role: 'tds',
        text: 'Dostane výzvu k vyjádření — jeho „razítko", že práce byla potřebná, je evidované přímo u změny.',
      },
      {
        role: 'reditel',
        text: 'Na rekapitulaci vidí všechny změny zakázky, kumulativní čísla i to, co je sporné. Investorská verze odchází bez nákladů a marží.',
        obrazek: '/marketing/rekapitulace.png',
        alt: 'Rekapitulace změn — tabulka s kumulativy a exportem pro investora',
      },
    ],
    vysledek:
      'Z fotky z výkopu je podepsaný změnový list zařazený v dodatku — doložený, odsouhlasený a fakturovatelný.',
  },
  {
    id: 'diry-v-deniku',
    skupina: 'Deník a důkazy',
    Ikona: BookOpen,
    problem: 'Deník má díry a dopisuje se zpětně',
    dnes: 'Deník se dopisuje v neděli večer za celý týden — a při sporu protistrana najde přesně ty dny, které chybí.',
    kroky: [
      {
        role: 'mistr',
        text: 'Den nadiktuje do telefonu, fotky přidá z místa, lidi a stroje převezme ze včerejška jedním ťuknutím. Počasí se doplní samo podle polohy stavby.',
        obrazek: '/marketing/teren-denik.png',
        alt: 'Terénní deník — diktování, fotky a převzetí včerejška',
      },
      {
        role: 'mistr',
        text: 'Večer den podepíše prstem — záznam se uzamkne. Oprava jde jen novým opravným záznamem, jako v účetnictví.',
      },
      {
        role: 'pripravar',
        text: 'V kanceláři vidí kalendář měsíce: podepsané dny zeleně, nepodepsané žlutě a DÍRY červeně — dřív, než je najde právník protistrany.',
        obrazek: '/marketing/denik.png',
        alt: 'Kalendář deníku — podepsané dny, nepodepsané a díry barevně',
      },
      {
        role: 'reditel',
        text: 'Kdykoli exportuje celý deník do PDF nebo Excelu — pro stavebníka, TDS nebo k archivaci.',
      },
    ],
    vysledek:
      'Deník je psaný průběžně, podepsaný a bez děr — právně použitelný důkaz, ne domácí úkol.',
  },
  {
    id: 'zakryvana-konstrukce',
    skupina: 'Deník a důkazy',
    Ikona: AlertTriangle,
    problem: 'Zítra se zakrývá konstrukce a dozor nikde',
    dnes: 'Výztuž se zalije, důkaz zmizí v betonu — a při přejímce se dohaduje, co v tom stropě vlastně je.',
    kroky: [
      {
        role: 'mistr',
        text: 'Odpoledne vyfotí výztuž a rovnou z deníku pošle VÝZVU dozoru k přejímce — s termínem „zítra se zakrývá". Foto před zakrytím je povinné, aplikace ho nenechá zapomenout.',
        obrazek: '/marketing/teren-denik.png',
        alt: 'Akční pás terénu — tlačítko VÝZVA hned vedle NÁLEZU',
      },
      {
        role: 'tds',
        text: 'Přijde se podívat (nebo se vyjádří na dálku) — vyjádření se eviduje s datem a jménem. Když nestihne termín, je to doložené taky.',
      },
      {
        role: 'pripravar',
        text: 'Výzva, foto i vyjádření zůstávají navěky u deníkového dne — a když z místa vznikne vícepráce, důkazní řetěz je kompletní.',
      },
    ],
    vysledek:
      'Co se zakrylo, je vyfocené, s výzvou a vyjádřením dozoru — spor o „co je v betonu" nevznikne.',
  },
  {
    id: 'honeni-podpisu',
    skupina: 'Papíry a lidé',
    Ikona: PenLine,
    problem: 'Honíme podpisy po stavbě s propiskou',
    dnes: 'Změnový list leží týden v autě, protože se nepotkal ten správný člověk s tou správnou propiskou.',
    kroky: [
      {
        role: 'pripravar',
        text: 'U změnového listu klikne na „Podpis protistrany" — vygeneruje se zabezpečený jednorázový odkaz s QR kódem.',
        obrazek: '/marketing/zmena.png',
        alt: 'Detail změny — tlačítka Podepsat ZL a Podpis protistrany',
      },
      {
        role: 'subdodavatel',
        text: 'Odkaz mu přijde mailem nebo SMS. Otevře, přečte shrnutí a podepíše prstem — bez registrace, bez instalace, klidně z kabiny bagru.',
        obrazek: '/marketing/podpis.png',
        alt: 'Veřejná podpisová stránka — dokument a podpis prstem',
      },
      {
        role: 'pripravar',
        text: 'Podpis je okamžitě vidět v aplikaci a rovnou v PDF dokumentu — s časem a IP adresou jako dokladem. Stejně se podepisují dodatky i protokoly.',
      },
    ],
    vysledek: 'Podepsáno během minut místo týdnů — a každý podpis má doklad kdy, kdo a odkud.',
  },
  {
    id: 'dochazka-dvakrat',
    skupina: 'Papíry a lidé',
    Ikona: Clock4,
    problem: 'Papírová docházka se přepisuje do mezd ručně',
    dnes: 'Mistr píše hodiny na papír, kancelář je přepisuje do mzdového systému — dvakrát ta samá práce a chyby po cestě.',
    kroky: [
      {
        role: 'mistr',
        text: 'Večer ťukne „Všichni 8 h", u dvou chlapů upraví dřívější odchod a uloží. Parta je založená jednou — dělníci aplikaci nepotřebují.',
        obrazek: '/marketing/teren-dochazka.png',
        alt: 'Docházka party v terénu — přepínače a hodiny s tlačítkem Všichni 8 h',
      },
      {
        role: 'pripravar',
        text: 'V kanceláři vidí měsíční mřížku osoby × dny se součty — kontrola před mzdami na jeden pohled.',
        obrazek: '/marketing/dochazka.png',
        alt: 'Měsíční přehled docházky s exportem pro OKbase',
      },
      {
        role: 'pripravar',
        text: 'Jedním tlačítkem stáhne Excel pro mzdový systém (OKbase) s osobními čísly. Výpočet mezd zůstává tam, kde je.',
      },
    ],
    vysledek: 'Hodiny se zapisují jednou — na stavbě. Do mezd jdou exportem, ne přepisováním.',
  },
  {
    id: 'penale-terminy',
    skupina: 'Peníze a vícepráce',
    Ikona: CalendarClock,
    problem: 'Penále za termín a nikdo neví, kolik dní máme kryto',
    dnes: 'Termín ze SOD se blíží, vícepráce nabraly týdny — ale nárok na prodloužení nikdo průběžně nedokládá.',
    kroky: [
      {
        role: 'pripravar',
        text: 'Na smlouvě zapíše milníky ze SOD i s penále — pevná částka za den, nebo procento z ceny Díla, se stropem.',
        obrazek: '/marketing/smlouva.png',
        alt: 'Detail smlouvy — milníky s penále a pozastávka',
      },
      {
        role: 'pripravar',
        text: 'U každé změny vyplní dopad na termín ve dnech. Schválené dopady se sčítají samy — to je nárok na prodloužení lhůty.',
      },
      {
        role: 'reditel',
        text: 'Hlídač hlásí: „Milník za 12 dní — kryto 20 dnů schválených změn." A když je zle, řekne i denní penále, o které se hraje.',
        obrazek: '/marketing/prehled.png',
        alt: 'Přehled s hlídači — co dnes hoří, s akcí',
      },
    ],
    vysledek: 'Spor o penále se vyhrává papírem — a ten papír tu vzniká průběžně, mimochodem.',
  },
  {
    id: 'faktura-bez-podkladu',
    skupina: 'Peníze a vícepráce',
    Ikona: FileSpreadsheet,
    problem: 'Konec měsíce a faktura nemá podklad',
    dnes: 'Prostavěnost se dohaduje po telefonu, soupis se přepisuje do Excelu a TDS podepisuje papír, který za týden nikdo nenajde.',
    kroky: [
      {
        role: 'pripravar',
        text: 'Založí zjišťovací protokol za měsíc a naklikne čerpání v procentech proti soupisu prací — částky se počítají samy, včetně toho, co už bylo čerpáno dříve.',
        obrazek: '/marketing/protokol.png',
        alt: 'Zjišťovací protokol — položky soupisu s čerpáním za období a částkami',
      },
      {
        role: 'pripravar',
        text: 'Do protokolu se dají zahrnout i podepsané změnové listy — vícepráce se fakturují spolu s běžným čerpáním, ne „někdy příště".',
      },
      {
        role: 'tds',
        text: 'Odsouhlasí protokol podpisem — na místě prstem, nebo přes zabezpečený odkaz z kanceláře. Podpis je v PDF s datem.',
        obrazek: '/marketing/podpis.png',
        alt: 'Podpis protistrany přes zabezpečený odkaz',
      },
      {
        role: 'reditel',
        text: 'Faktura odchází s odsouhlaseným protokolem jako přílohou — o prostavěnosti se nediskutuje, je podepsaná.',
      },
    ],
    vysledek:
      'Měsíční fakturace stojí na odsouhlaseném protokolu, ne na dohadech — a vícepráce v ní nechybí.',
  },
  {
    id: 'zapomenute-zadrzne',
    skupina: 'Peníze a vícepráce',
    Ikona: PiggyBank,
    problem: 'Na zádržné se po dvou letech zapomene',
    dnes: 'Investor drží 5 % z ceny Díla. Půlka se uvolní předáním, zbytek po záruce — jenže za dva roky si na to nikdo nevzpomene a peníze leží u investora.',
    kroky: [
      {
        role: 'pripravar',
        text: 'U smlouvy nastaví pozastávku s tranšemi a spouštěči (předání díla, konec záruky, odklad v měsících) — jednou, při založení.',
        obrazek: '/marketing/smlouva.png',
        alt: 'Detail smlouvy — pozastávka s tranšemi, procenty a spouštěči',
      },
      {
        role: 'pripravar',
        text: 'Při předání díla zaeviduje předávací protokol s podpisem — termíny uvolnění všech tranší se dopočtou samy od data předání.',
      },
      {
        role: 'reditel',
        text: 'Až termín přijde — klidně za 24 měsíců — hlídač sám připomene: „Tranše 2 % je uvolnitelná, vyfakturujte ji investorovi." U subdodavatelů to platí zrcadlově.',
        obrazek: '/marketing/prehled.png',
        alt: 'Přehled s hlídači — připomínka uvolnitelné tranše',
      },
    ],
    vysledek:
      'Zádržné se nehlídá v hlavě ani v šuplíku — systém ho připomene přesně v den, kdy na něj vzniká nárok.',
  },
  {
    id: 'revize-pd',
    skupina: 'Papíry a lidé',
    Ikona: Ruler,
    problem: 'Přišla nová revize PD a staví se podle staré',
    dnes: 'Revize dorazí mailem, na stavbě koluje vytištěná stará verze — a vícepráce, kterou revize způsobila, si nikdo nespojí s papírem.',
    kroky: [
      {
        role: 'pripravar',
        text: 'Nahraje novou verzi výkresu — stará zůstává v historii, ale platná je vždy jen jedna. Systém se hned zeptá: „Vznikla z téhle revize vícepráce?"',
        obrazek: '/marketing/vykresy.png',
        alt: 'Výkresy — platná verze v2 a nahrání nové verze',
      },
      {
        role: 'mistr',
        text: 'V terénu otevře výkres a vidí VŽDY platnou verzi — žádné „já měl ještě tu starou". Když v revizi objeví změnu, založí nález přímo z výkresu.',
      },
      {
        role: 'pripravar',
        text: 'Nález z revize nese důkazní vazbu na konkrétní verzi výkresu — u změny je navěky vidět, ze které revize vícepráce vznikla.',
      },
    ],
    vysledek:
      'Staví se podle platné dokumentace a každá vícepráce z revize má stopu k verzi, která ji způsobila.',
  },
  {
    id: 'prace-na-riziko',
    skupina: 'Peníze a vícepráce',
    Ikona: Scale,
    problem: 'Investor změnu neuznává, ale práce už běží',
    dnes: 'Stavba nemůže stát, tak se dělá bez podpisu — a po půl roce se řeší, jestli vůbec někdo doloží, co se udělalo a za čí peníze.',
    kroky: [
      {
        role: 'mistr',
        text: 'Práce se provede — ale den je v deníku, fotky mají čas a GPS a u změny je vidět „provedeno".',
      },
      {
        role: 'pripravar',
        text: 'Změnový list bez podpisu investora svítí červeně jako „na riziko!" — v seznamu změn i v rekapitulaci. Nic se tváří, že je v pořádku.',
        obrazek: '/marketing/zmeny.png',
        alt: 'Seznam změn — červené „na riziko!" u práce provedené bez podpisu',
      },
      {
        role: 'tds',
        text: 'Vyjádření dozoru („práce byla potřebná a provedená") je evidované u změny — klíčový důkaz pro jednání.',
      },
      {
        role: 'reditel',
        text: 'Spornou změnu označí a sleduje zvlášť; hlídač hlásí celkový objem „na riziko" — vědomé rozhodnutí, ne zapomenuté riziko. Když se rozhodne náklad nést, potvrdí „neseme sami" podpisem.',
        obrazek: '/marketing/rekapitulace.png',
        alt: 'Rekapitulace — sporné změny a objem provedeného bez podpisu',
      },
    ],
    vysledek:
      'I práce na riziko má důkazní řetěz: deník, fotky, vyjádření dozoru a vědomé rozhodnutí ředitele — ne díru v papírech.',
  },
  {
    id: 'verejna-zakazka-222',
    skupina: 'Peníze a vícepráce',
    Ikona: Landmark,
    problem: 'Veřejná zakázka a limit § 222',
    dnes: 'U veřejné zakázky smí změny bez nového řízení jen do 15 % ceny — jenže čerpání limitu nikdo průběžně nepočítá a přešvihnutí je průšvih pro obě strany.',
    kroky: [
      {
        role: 'pripravar',
        text: 'U hlavní SOD zaškrtne „veřejná zakázka" — od té chvíle systém počítá čerpání limitu z absolutních hodnot podepsaných změn (méněpráce čerpání nesnižují).',
      },
      {
        role: 'pripravar',
        text: 'Při podpisu změnového listu, který by limit 15 % prolomil, systém podpis ZASTAVÍ a řekne přesně, kolik je vyčerpáno a kolik by bylo po podpisu.',
        obrazek: '/marketing/zmena.png',
        alt: 'Detail změny — podpis ZL s hlídačem limitu § 222',
      },
      {
        role: 'reditel',
        text: 'Prolomit limit smí jen role s právem schvalovat změny — vědomě, s auditní stopou. Čerpání je vidět i v rekapitulaci a jejím exportu.',
        obrazek: '/marketing/rekapitulace.png',
        alt: 'Rekapitulace — čerpání změn vůči ceně SOD s limitem § 222',
      },
    ],
    vysledek:
      'Limit § 222 se hlídá při každém podpisu automaticky — žádné překvapení při kontrole zadavatele.',
  },
  {
    id: 'ukoly-po-telefonu',
    skupina: 'Papíry a lidé',
    Ikona: ClipboardCheck,
    problem: 'Úkoly se zadávají po telefonu a zapomínají se',
    dnes: '„Řekni Frantovi, ať doplní zábradlí" — řečeno v poledne u buňky, zapomenuto do večera. A vada z pochůzky skončí v bloku, který se ztratí.',
    kroky: [
      {
        role: 'mistr',
        text: 'Úkol zadá na místě: název, komu (subdodavatel nebo profese) a termín rychlou volbou „Zítra". Vadu založí i s fotkou.',
        obrazek: '/marketing/teren-ukoly.png',
        alt: 'Úkoly a vady v terénu — karty s termínem a tlačítkem ZAČÍT',
      },
      {
        role: 'subdodavatel',
        text: 'Parta vidí své úkoly se stavem (nový → v řešení → hotovo) — nic nevisí jen v něčí hlavě.',
      },
      {
        role: 'pripravar',
        text: 'Kancelář vidí všechny úkoly a vady zakázky včetně fotek — co se vleče, je vidět dřív, než z toho bude reklamace.',
      },
    ],
    vysledek: 'Zadané na místě, s fotkou a termínem — úkol se nedá „nezaslechnout".',
  },
  {
    id: 'kde-je-ta-fotka',
    skupina: 'Deník a důkazy',
    Ikona: Camera,
    problem: 'Kde je ta fotka jímky z minulého měsíce?',
    dnes: 'Fotky jsou v pěti telefonech, třech WhatsApp vláknech a jednom mailu — a ta jediná důležitá se hledá hodinu.',
    kroky: [
      {
        role: 'mistr',
        text: 'Fotí normálně do kontextu — k deníku, nálezu, vadě. Nic navíc dělat nemusí.',
      },
      {
        role: 'pripravar',
        text: 'Fotogalerie zakázky ukáže všechny fotky napříč deníkem, nálezy, změnami i vadami — seskupené po dnech, s filtrem podle objektu a dne.',
        obrazek: '/marketing/fotogalerie.png',
        alt: 'Fotogalerie stavby — fotky po dnech s filtrem podle objektu',
      },
      {
        role: 'reditel',
        text: 'U každé fotky je vidět čas pořízení a odkud pochází (deník, nález…) — jedním klikem se otevře zdroj. Důkaz se najde za minutu.',
      },
    ],
    vysledek:
      'Jedna galerie za celou stavbu — fotka se hledá podle dne a objektu, ne po telefonech party.',
  },
];

const SKUPINY: Skupina[] = ['Peníze a vícepráce', 'Deník a důkazy', 'Papíry a lidé'];

/** Karty problémů ve skupinách — sdílené desktopovým menu a mobilním výběrem. */
function SeznamProblemu({
  aktivniId,
  onVybrat,
}: {
  aktivniId: string;
  onVybrat: (id: string) => void;
}) {
  return (
    <>
      {SKUPINY.map((skupina) => (
        <section key={skupina} className="mb-5">
          <p className="px-1 pb-2 text-xs font-semibold tracking-wider text-text-secondary uppercase">
            {skupina}
          </p>
          <ul className="flex flex-col gap-1.5">
            {SCENARE.filter((x) => x.skupina === skupina).map((s) => {
              const vybrany = s.id === aktivniId;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    aria-current={vybrany ? 'true' : undefined}
                    onClick={() => onVybrat(s.id)}
                    className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left text-sm font-semibold transition-colors duration-100 ${
                      vybrany
                        ? 'border-primary bg-primary text-white shadow-md'
                        : 'border-border bg-surface hover:border-border-strong hover:bg-primary-tint'
                    }`}
                  >
                    <s.Ikona
                      size={18}
                      strokeWidth={1.75}
                      aria-hidden="true"
                      className={`mt-0.5 shrink-0 ${vybrany ? 'text-accent' : 'text-primary'}`}
                    />
                    {s.problem}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </>
  );
}

export function ScenarePage() {
  const [aktivniId, setAktivniId] = useState(SCENARE[0]!.id);
  // Mobilní výběr problému — otevírá se přes celou obrazovku.
  const [vyberOtevren, setVyberOtevren] = useState(false);
  const aktivni = SCENARE.find((s) => s.id === aktivniId) ?? SCENARE[0]!;
  // Pravý panel scrolluje samostatně — při výběru scénáře zpět nahoru.
  const obsahRef = useRef<HTMLDivElement>(null);
  const vyber = (id: string) => {
    setAktivniId(id);
    obsahRef.current?.scrollTo({ top: 0 });
    window.scrollTo({ top: 0 });
  };

  return (
    <main className="flex min-h-screen flex-col bg-bg lg:h-screen lg:overflow-hidden">
      <VerejnaHlavicka />

      <section className="shrink-0 bg-primary pb-6 text-white">
        <div className="mx-auto w-full max-w-6xl px-6 pt-5">
          <h1 className="text-2xl font-bold text-balance text-white lg:text-3xl">
            Zná to každá stavba. Takhle se to řeší v Helfru.
          </h1>
          <p className="mt-1.5 max-w-prose text-base text-primary-tint">
            Vyberte problém — a projděte si krok za krokem, kdo co udělá. Náhledy jsou skutečné
            obrazovky aplikace, kliknutím je zvětšíte.
          </p>
        </div>
      </section>

      {/* Desktop: panelová řada přes CELOU šířku okna — posuvník obsahu je až
          na kraji obrazovky, ne u textu. Zarovnání s max-w-6xl drží dopočítané
          vnitřní okraje (offset středění + 1,5rem). Mobil: běžný tok. */}
      <div className="mx-auto grid w-full max-w-6xl flex-1 items-start gap-6 px-6 py-5 lg:flex lg:max-w-none lg:min-h-0 lg:items-stretch lg:gap-0 lg:px-0 lg:py-0">
        {/* Mobil: lišta s vybraným problémem drží u horního okraje; ťuknutí
            otevře výběr přes CELOU obrazovku (nativní select se 13 položkami
            se na displej nevešel a špatně se v něm trefovalo). */}
        <div className="sticky top-0 z-20 -mx-6 -mt-5 min-w-0 bg-bg px-6 py-3 shadow-sm lg:hidden">
          <button
            type="button"
            aria-haspopup="dialog"
            aria-expanded={vyberOtevren}
            onClick={() => setVyberOtevren(true)}
            className="flex min-h-12 w-full items-center gap-3 rounded-xl bg-primary px-4 text-left text-base font-semibold text-white shadow-md"
          >
            <aktivni.Ikona size={18} strokeWidth={2} aria-hidden="true" className="shrink-0 text-accent" />
            <span className="min-w-0 flex-1 truncate">{aktivni.problem}</span>
            <span className="flex shrink-0 items-center gap-1 text-sm font-bold text-accent">
              změnit
              <ChevronDown size={20} strokeWidth={2.5} aria-hidden="true" />
            </span>
          </button>
        </div>

        {vyberOtevren && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Vyberte problém ze stavby"
            className="fixed inset-0 z-50 flex flex-col bg-bg lg:hidden"
            onKeyDown={(e) => e.key === 'Escape' && setVyberOtevren(false)}
          >
            <div className="flex shrink-0 items-center justify-between gap-3 bg-primary px-6 py-3 text-white">
              <h2 className="text-lg font-bold text-white">Vyberte problém</h2>
              <button
                type="button"
                aria-label="Zavřít výběr"
                onClick={() => setVyberOtevren(false)}
                className="flex size-11 shrink-0 items-center justify-center rounded-full bg-white/15 hover:bg-white/25"
              >
                <X size={22} strokeWidth={2} aria-hidden="true" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <SeznamProblemu
                aktivniId={aktivniId}
                onVybrat={(id) => {
                  vyber(id);
                  setVyberOtevren(false);
                }}
              />
            </div>
          </div>
        )}

        {/* Menu problémů (desktop) — vlastní posuvník, nezávislý na obsahu vpravo. */}
        <nav
          aria-label="Nejčastější problémy"
          className="hidden lg:block lg:h-full lg:w-[calc(21.5rem+max(1.5rem,(100vw-72rem)/2+1.5rem))] lg:shrink-0 lg:overflow-y-auto lg:py-6 lg:pr-6 lg:pl-[max(1.5rem,calc((100vw-72rem)/2+1.5rem))]"
        >
          <SeznamProblemu aktivniId={aktivniId} onVybrat={vyber} />
        </nav>

        {/* Scénář — panel scrolluje samostatně, posuvník na kraji okna. */}
        <div ref={obsahRef} className="min-w-0 lg:h-full lg:flex-1 lg:overflow-y-auto lg:py-6">
          <article
            key={aktivni.id}
            className="lg:pl-2 lg:pr-[max(1.5rem,calc((100vw-72rem)/2+1.5rem))]"
          >
            <h2 className="text-2xl font-bold text-balance lg:text-3xl">{aktivni.problem}</h2>
            <p className="mt-2 flex items-start gap-2 rounded-xl border border-error-border bg-error-bg p-3 text-base text-error-text">
              <ShieldQuestion
                size={18}
                strokeWidth={2}
                aria-hidden="true"
                className="mt-0.5 shrink-0"
              />
              <span>
                <strong>Jak to bolí dnes:</strong> {aktivni.dnes}
              </span>
            </p>

            <ol className="mt-6 flex flex-col">
              {aktivni.kroky.map((k, i) => {
                const role = ROLE[k.role];
                const posledni = i === aktivni.kroky.length - 1;
                return (
                  <li key={i} className="relative flex gap-4 pb-8">
                    {/* Osa: tečka role + spojnice k dalšímu kroku */}
                    {!posledni && (
                      <span
                        aria-hidden="true"
                        className="absolute top-9 left-[15px] h-[calc(100%-2rem)] w-0.5 bg-border"
                      />
                    )}
                    <span
                      aria-hidden="true"
                      className={`z-10 mt-1 flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${role.tecka} ${
                        k.role === 'mistr' ? '!text-text-heading' : ''
                      }`}
                    >
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold tracking-wide uppercase ${role.chip}`}
                      >
                        {role.nazev}
                      </span>
                      <p className="mt-1.5 text-base">{k.text}</p>
                      {k.obrazek && k.alt && (
                        <div className="mt-3 max-w-xl">
                          <NahledAplikace src={k.obrazek} alt={k.alt} />
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>

            <p className="rounded-xl border border-success-border bg-success-bg p-4 text-base font-semibold text-success-text">
              Výsledek: {aktivni.vysledek}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-border pt-6">
              <Link
                to="/registrace"
                className="inline-flex min-h-12 items-center gap-2 rounded-lg bg-accent px-5 text-base font-bold text-text-heading transition-colors duration-100 hover:bg-accent-hover"
              >
                Vyzkoušet na vlastní firmě — zdarma
                <ArrowRight size={17} strokeWidth={2.25} aria-hidden="true" />
              </Link>
              <Link
                to="/funkce"
                className="inline-flex min-h-12 items-center px-2 font-semibold text-primary underline-offset-2 hover:underline"
              >
                Všechny funkce do detailu →
              </Link>
            </div>
          </article>
        </div>
      </div>

      <footer className="shrink-0 bg-primary-pressed py-4 text-center text-sm text-primary-tint">
        {APP_NAME} — stavební deník a hlídač víceprací
      </footer>
    </main>
  );
}
